import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Mail, Phone, Plus, Trash2, X } from 'lucide-react';
import { useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { saveContactEdit } from './save-contact-edit';
import type { ContactDetails } from './types';
import type { Ref } from 'react';
import type { Id } from '@minty/shared/ids';
import { useCan } from '~/hooks/use-can';
import { useConfirm } from '~/components/confirm-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import { noAutofill } from '~/lib/autofill';
import {
  appErrorCode,
  appErrorOwnerName,
  appErrorValue,
} from '~/lib/app-error';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

/**
 * Making a person, and editing one. Every edit lands here, so the panel beside
 * it only shows.
 *
 * A name and the hide box wait for Save. Save also commits a pending
 * channel before closing. Adding may ask whose history is being moved;
 * declining keeps the dialog open. Removing a channel stays a separate act
 * and is refused once anything is filed under it.
 */

const createSchema = z.object({
  name: z.string().trim(),
  phoneNumber: z.string().trim(),
  email: z.string().trim(),
});

type CreateForm = z.infer<typeof createSchema>;

const editSchema = z.object({
  name: z.string().trim(),
  isHidden: z.boolean(),
});

type EditForm = z.infer<typeof editSchema>;

type OwnedChannel = NonNullable<ContactDetails>['channels'][number];

/**
 * Where a search that found nobody belongs in the form that creates them.
 *
 * The receptionist has already typed the thing they were looking for; asking
 * them to type it again in the right box is the sort of small insult a dialog
 * opened *from* a search should not commit.
 */
export function prefillFromQuery(query: string): Partial<CreateForm> {
  const value = query.trim();

  if (value === '') {
    return {};
  }

  if (value.includes('@')) {
    return { email: value };
  }

  /** Digits, and the punctuation a number gets written with. Anything else is
   *  a name — whether it is really a number is the server's call. */
  return /^[+\d][\d\s().-]*$/.test(value)
    ? { phoneNumber: value }
    : { name: value };
}

function CreateContactDialogContent({
  isOpen,
  onOpenChange,
  onCreated,
  query = '',
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (contactId: Id<'contacts'>) => void;
  query?: string;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: createContact } = useMutation(
    trpc.contacts.ownership.createContact.mutationOptions(),
  );
  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      email: '',
      ...prefillFromQuery(query),
    },
  });
  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = async (values: CreateForm) => {
    /** A record owning nothing is unreachable and unfindable, so a name on its
     *  own is not a contact. The server refuses it too; this only says so
     *  without the round trip. */
    if (values.phoneNumber === '' && values.email === '') {
      form.setError('phoneNumber', {
        message: t('Give a number or an address.'),
      });

      return;
    }

    try {
      const contactId = await createContact({
        values: [values.phoneNumber, values.email],
        ...(values.name ? { name: values.name } : {}),
      });

      onOpenChange(false);
      onCreated(contactId);
    } catch (error) {
      const code = appErrorCode(error);
      /** Echoed back exactly as it was sent, so the mark lands on the box the
       *  refusal is actually about. */
      const isEmail = appErrorValue(error) === values.email;
      const field = isEmail ? 'email' : 'phoneNumber';

      if (code === 'not_a_channel') {
        form.setError(field, {
          message: isEmail
            ? t('That is not an email address.')
            : t('That is not a phone number.'),
        });

        return;
      }

      if (code === 'owned_elsewhere') {
        form.setError(field, {
          message: t('{{owner}} already has this — open their card instead.', {
            owner: appErrorOwnerName(error) ?? t('Somebody else'),
          }),
        });

        return;
      }

      notify.error(t('Unable to create this contact.'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('Close', { ns: 'translation' })}>
        <Form.Root
          form={form}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>{t('New contact')}</DialogTitle>
            <DialogDescription>
              {t("Enter the patient's details")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('First name')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      disabled={isSubmitting}
                      placeholder={t('Jan')}
                      {...noAutofill}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Phone number')}</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      disabled={isSubmitting}
                      placeholder="+48 500 000 000"
                      {...noAutofill}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Email address')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      disabled={isSubmitting}
                      placeholder="jan.kowalski@example.com"
                      {...noAutofill}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t('Cancel', { ns: 'translation' })}
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('Create')}
            </Button>
          </DialogFooter>
        </Form.Root>
      </DialogContent>
    </Dialog>
  );
}

function EditContactDialogContent({
  contact,
  isOpen,
  onOpenChange,
}: {
  contact: NonNullable<ContactDetails>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: updateContact } = useMutation(
    trpc.contacts.directory.updateContact.mutationOptions(),
  );
  const pendingChannel = useRef<PendingChannelHandle>(null);
  const [channelSaving, setChannelSaving] = useState(false);
  const form = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: contact.name ?? '', isHidden: contact.isHidden },
  });
  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = async (values: EditForm) => {
    try {
      const saved = await saveContactEdit(
        pendingChannel.current?.savePending,
        () =>
          updateContact({
            contactId: contact.id,
            ...(values.name ? { name: values.name } : {}),
            isHidden: values.isHidden,
          }),
      );
      if (!saved) return;

      onOpenChange(false);
      notify.success(t('Contact updated.'));
    } catch {
      notify.error(t('Unable to update this contact.'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('Close', { ns: 'translation' })}>
        <Form.Root
          form={form}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>{t('Edit contact')}</DialogTitle>
            <DialogDescription>
              {t("Change the patient's details")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('First name')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      disabled={isSubmitting || channelSaving}
                      placeholder={t('Jan')}
                      {...noAutofill}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ChannelSection
              contact={contact}
              pendingRef={pendingChannel}
              disabled={isSubmitting || channelSaving}
              onSavingChange={setChannelSaving}
            />
            <TagSection contact={contact} />
            <FormField
              control={form.control}
              name="isHidden"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2">
                  <FormControl>
                    <Checkbox
                      disabled={isSubmitting || channelSaving}
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  {/* Hidden is not deleted: everything still arrives and still
                      resolves to them, it just reaches no list. */}
                  <FormLabel className="font-normal">
                    {t('Hide contact')}
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting || channelSaving}
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t('Cancel', { ns: 'translation' })}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || channelSaving}
              className="flex-1"
            >
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('Save')}
            </Button>
          </DialogFooter>
        </Form.Root>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Every channel this person owns, and the two things you can do to one.
 *
 * Which is the default is not one of them. It is still shown — the panel draws
 * a border round it — but choosing it was a radio on every row for a fact that
 * settles itself: the first of each kind to arrive, replaced if it leaves. On a
 * contact owning one number and one address, which is nearly all of them, the
 * control had nothing to offer at all.
 */
type PendingChannelHandle = { savePending: () => Promise<boolean> };
type PendingChannelProps = {
  pendingRef: Ref<PendingChannelHandle>;
  disabled: boolean;
  onSavingChange: (saving: boolean) => void;
};

function ChannelSection({
  contact,
  ...pending
}: { contact: NonNullable<ContactDetails> } & PendingChannelProps) {
  const { t } = useTranslation('tasks');
  const phones = contact.channels.filter((channel) => channel.kind === 'phone');
  const emails = contact.channels.filter((channel) => channel.kind === 'email');

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('Channels')}</Label>
      <ChannelRows icon={Phone} channels={phones} />
      <ChannelRows icon={Mail} channels={emails} />
      <AddChannelRow contactId={contact.id} {...pending} />
    </div>
  );
}

function ChannelRows({
  icon: Icon,
  channels,
}: {
  icon: typeof Mail;
  channels: Array<OwnedChannel>;
}) {
  return (
    <div className="flex flex-col">
      {channels.map((channel) => (
        <div key={channel.value} className="flex h-8 items-center gap-2">
          <Icon
            className="size-3.5 shrink-0 text-muted-foreground"
            strokeWidth={2.5}
          />
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
            {channel.value}
          </span>
          <RemoveChannelButton channel={channel} />
        </div>
      ))}
    </div>
  );
}

/**
 * What we know about this person that is not a channel and not a task.
 *
 * Each act stands alone, like the channels above: a tag is added or taken away
 * on the click, and there is nothing here for Save to hold.
 */
function TagSection({ contact }: { contact: NonNullable<ContactDetails> }) {
  const { t } = useTranslation('tasks');

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('Tags')}</Label>
      <div className="flex flex-wrap items-center gap-1">
        {contact.tags.map((tag) => (
          <RemovableTag key={tag} contactId={contact.id} tag={tag} />
        ))}
        <AddTagPopover contact={contact} />
      </div>
    </div>
  );
}

function RemovableTag({
  contactId,
  tag,
}: {
  contactId: Id<'contacts'>;
  tag: string;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: removeTag } = useMutation(
    trpc.contacts.tags.removeTag.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleRemove = async () => {
    setIsSaving(true);

    try {
      await removeTag({ contactId, tag });
    } catch {
      notify.error(t('Unable to remove {{value}}.', { value: tag }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Badge shape="pill" tone="info" className="pr-1">
      {tag}
      <button
        type="button"
        disabled={isSaving}
        onClick={() => void handleRemove()}
        aria-label={t('Remove {{value}}', { value: tag })}
        className="flex cursor-pointer items-center rounded-full opacity-70 hover:opacity-100 disabled:pointer-events-none"
      >
        {/* Sized here rather than left to the badge: its rule is `[&>svg]`, a
            direct child, and this one is inside the button. */}
        <X className="size-3" strokeWidth={2.5} />
      </button>
    </Badge>
  );
}

/**
 * Pick one that already exists, or make one.
 *
 * A popover rather than an inline list so the dialog does not change height
 * while you type. `listTags` is only fetched while this is on screen — Radix
 * does not mount a closed dialog's content, so a panel sitting on a contact is
 * not holding a 500-row read open.
 */
function AddTagPopover({ contact }: { contact: NonNullable<ContactDetails> }) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: addTag } = useMutation(
    trpc.contacts.tags.addTag.mutationOptions(),
  );
  const { data: known = [] } = useQuery(
    trpc.contacts.tags.listTags.queryOptions(),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const typed = value.trim();
  const folded = typed.toLowerCase();
  /** Already on this person is not a suggestion; the badge beside it says so
   *  and adding again would be a no-op the server quietly swallows. */
  const matches = known.filter(
    (tag) => !contact.tags.includes(tag) && tag.toLowerCase().includes(folded),
  );
  /** Offered only when it is genuinely new, case-folded — otherwise `VIP` and
   *  `vip` become two tags that read as one. */
  const isNew =
    typed !== '' && !known.some((tag) => tag.toLowerCase() === folded);

  const add = async (tag: string) => {
    setIsSaving(true);

    try {
      await addTag({ contactId: contact.id, tag });
      setValue('');
      setIsOpen(false);
    } catch {
      notify.error(t('Unable to add {{value}}.', { value: tag }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);

        if (!open) setValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="muted" size="xs" shape="pill">
          <Plus className="size-3" strokeWidth={2.5} />
          {t('Tag')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <Input
          autoFocus
          disabled={isSaving}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();

            const first = matches[0];

            if (first !== undefined) void add(first);
            else if (isNew) void add(typed);
          }}
          placeholder={t('Find or create a tag')}
          aria-label={t('Find or create a tag')}
          className="h-8 text-sm"
          {...noAutofill}
        />
        <div className="mt-1 flex max-h-48 flex-col overflow-auto">
          {matches.map((tag) => (
            <button
              key={tag}
              type="button"
              disabled={isSaving}
              onClick={() => void add(tag)}
              className="cursor-pointer truncate rounded-sm px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent"
            >
              {tag}
            </button>
          ))}
          {isNew && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => void add(typed)}
              className="cursor-pointer truncate rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
            >
              {t('Create “{{value}}”', { value: typed })}
            </button>
          )}
          {matches.length === 0 && !isNew && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {t('Nothing to add')}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Offered only for a channel nothing has ever been filed under, which is
 *  exactly the case where removing it can strand nothing. */
function RemoveChannelButton({ channel }: { channel: OwnedChannel }) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: removeEmptyChannel } = useMutation(
    trpc.contacts.ownership.removeUnusedChannel.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);

  if (channel.hasHistory) {
    return null;
  }

  const handleRemove = async () => {
    setIsSaving(true);

    try {
      await removeEmptyChannel({
        channel:
          channel.kind === 'phone'
            ? { kind: 'phone', phoneNumber: channel.value }
            : { kind: 'email', email: channel.value },
      });
      notify.success(t('{{value}} removed.', { value: channel.value }));
    } catch {
      notify.error(t('Unable to remove {{value}}.', { value: channel.value }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Button
      type="button"
      variant="muted"
      tone="destructive"
      size="icon-xs"
      shape="pill"
      disabled={isSaving}
      onClick={() => void handleRemove()}
      aria-label={t('Remove {{value}}', { value: channel.value })}
    >
      <Trash2 className="size-3.5" strokeWidth={2.5} />
    </Button>
  );
}

/**
 * Add a number or an address to this person.
 *
 * This is also how a channel *moves*: type it here and it comes from wherever it
 * was, once you have confirmed who it is being taken from. Plain state rather
 * than a form, because a form inside the dialog's form is not a thing the
 * browser will render.
 */
function AddChannelRow({
  contactId,
  pendingRef,
  disabled,
  onSavingChange,
}: { contactId: Id<'contacts'> } & PendingChannelProps) {
  const { t } = useTranslation('tasks');
  const confirm = useConfirm();
  const trpc = useTRPC();
  const { mutateAsync: addChannel } = useMutation(
    trpc.contacts.ownership.addChannel.mutationOptions(),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    setValue('');
    setError(null);
    setIsOpen(false);
  };
  const submit = async (takeFromOwner: boolean): Promise<boolean> => {
    if (value.trim() === '') {
      return true;
    }

    if (isSaving && !takeFromOwner) return false;
    setIsSaving(true);
    onSavingChange(true);

    try {
      await addChannel({ contactId, value, takeFromOwner });
      notify.success(t('{{value}} added.', { value }));
      close();
      return true;
    } catch (caught) {
      const code = appErrorCode(caught);

      if (code === 'not_a_channel') {
        setError(t('That is not a phone number or an email address.'));
        return false;
      }

      /** Never taken off somebody silently: the server refuses until the
       *  receptionist has been told whose it is. */
      if (code === 'owned_elsewhere') {
        const ownerName = appErrorOwnerName(caught) ?? t('Somebody else');

        if (
          await confirm({
            title: t(
              '{{value}} belongs to {{owner}}. Move it here, with its history?',
              { value, owner: ownerName },
            ),
            confirmLabel: t('Move', { ns: 'translation' }),
            isDestructive: true,
          })
        ) {
          return await submit(true);
        }

        return false;
      }

      setError(t('Unable to add {{value}}.', { value }));
      return false;
    } finally {
      setIsSaving(false);
      onSavingChange(false);
    }
  };

  useImperativeHandle(pendingRef, () => ({
    savePending: () => (isOpen ? submit(false) : Promise.resolve(true)),
  }));

  if (!isOpen) {
    return (
      <Button
        type="button"
        variant="muted"
        size="xs"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        className="mt-1 self-start"
      >
        <Plus className="size-3" strokeWidth={2.5} />
        {t('Add a number or address')}
      </Button>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          disabled={isSaving || disabled}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }

            if (event.key === 'Escape' && !isSaving) {
              event.preventDefault();
              close();
            }
          }}
          placeholder={t('Number or email address')}
          aria-label={t('Number or email address')}
          aria-invalid={error !== null}
          className="h-8 text-sm"
          {...noAutofill}
        />
      </div>
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function CreateContactDialog(
  props: Parameters<typeof CreateContactDialogContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <CreateContactDialogContent {...props} /> : null;
}

export function EditContactDialog(
  props: Parameters<typeof EditContactDialogContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <EditContactDialogContent {...props} /> : null;
}
